import { Action, ActionClass, getFetchActionTypeForRequestClass, ReduxAction } from 'acscp/actions';
import { BaseRequest, RequestClass } from 'acscp/api/requests';
import { actionMatches, FetchError, FetchSuccess, ResetStore } from 'acscp/actions';
import { ResponseErrorTypes } from 'acscp/api/responses';
import { ErrorDetails } from 'acscp/api/models/errorDetails';

export type Reducer<TState, TAction extends Action> = (state: TState, action: TAction) => TState;

export type SuccessfulRequestReducer<TState, TPayload, TRequest extends BaseRequest<TPayload>> =
    (state: TState, payload: TPayload, request: TRequest) => TState;

export type FailedRequestReducer<TState, TRequest extends BaseRequest<any | undefined>> =
    (state: TState, responseType: ResponseErrorTypes, error: ErrorDetails, request: TRequest) => TState;

interface Case<TState, TAction extends Action> {
    actionType: string;
    reducer: Reducer<TState, TAction>;
}

type CaseList<TState> = Array<Case<TState, Action>>;

export class ReducerBuilder<TState> {
    private cases: CaseList<TState> = [];

    constructor(private initialState: TState) {
    }

    private addCase<TAction extends Action>(newCase: Case<TState, TAction>): ReducerBuilder<TState> {
        const newReducer = new ReducerBuilder(this.initialState);
        newReducer.cases = [...this.cases, newCase];
        return newReducer;
    }

    actionCase<TAction extends Action>(actionClass: ActionClass<TAction>,
                                       reducer: Reducer<TState, TAction>)
            : ReducerBuilder<TState> {

        const newCase: Case<TState, TAction> = { actionType: actionClass.name, reducer };
        return this.addCase(newCase);
    }

    successfulRequestCase<TPayload, TRequest extends BaseRequest<TPayload>>(
        requestClass: RequestClass<TRequest>,
        reducer: SuccessfulRequestReducer<TState, TPayload, TRequest>
    ): ReducerBuilder<TState> {

        const newCase: Case<TState, FetchSuccess<TPayload, TRequest>> = {
            actionType: getFetchActionTypeForRequestClass(FetchSuccess, requestClass),
            reducer: (state, action) => reducer(state, action.response, action.request),
        };

        return this.addCase(newCase);
    }

    failedRequestCase<TRequest extends BaseRequest<any | undefined>>(
        requestClass: RequestClass<TRequest>,
        reducer: FailedRequestReducer<TState, TRequest>
    ): ReducerBuilder<TState> {

        const newCase: Case<TState, FetchError<TRequest>> = {
            actionType: getFetchActionTypeForRequestClass(FetchError, requestClass),
            reducer: (state, action) => reducer(state, action.responseType, action.error, action.request),
        };

        return this.addCase(newCase);
    }

    build(): (state: TState, action: ReduxAction<Action>) => TState {
        return (state: TState = this.initialState, action: ReduxAction<Action>) => {
            // Handle the store reset action.
            if (actionMatches(ResetStore, action)) {
                return this.initialState;
            }

            for (const { actionType, reducer } of this.cases) {
                if (actionMatches(actionType, action)) {
                    return reducer(state, action.payload);
                }
            }
            return state;
        };
    }
}
